/**
 * UUIDv7 — единственная форма идентификатора во всей системе (§Хребет данных 1.4).
 * ULID удалён из проекта полностью.
 *
 * Без зависимостей и без node:* — этот файл исполняется и в браузерном бандле UI,
 * который сам генерирует X-Request-Id и Idempotency-Key для своих POST.
 *
 * Реализация — RFC 9562 §5.7 метод 2: 48 бит времени, версия, 12-битный счётчик в rand_a,
 * вариант, 62 бита случайности. Счётчик даёт монотонность внутри одной миллисекунды.
 *
 * crypto.randomUUID({version: 7}) НЕ использовать: проверено на Node 26.7.0 — опция
 * игнорируется, возвращается v4.
 */

import { nowMs as clockNowMs } from './time.ts';

export const UUID_V7_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

const HEX: readonly string[] = /* @__PURE__ */ (() => {
  const t: string[] = new Array(256);
  for (let i = 0; i < 256; i++) t[i] = i.toString(16).padStart(2, '0');
  return t;
})();

const MAX_COUNTER = 0x0fff;

/**
 * Счётчик засевается только младшими 10 битами (0…1023), а не всеми двенадцатью.
 * Засев по всему диапазону оставляет в худшем случае один инкремент до переполнения —
 * проверено: на 200 000 подряд лексикографический порядок ломался примерно каждые
 * 1400 идентификаторов. Здесь запас — не меньше 3072 инкрементов на миллисекунду.
 */
const COUNTER_SEED_MASK = 0x03ff;

interface CounterState {
  ms: number;
  counter: number;
}

/** Состояние пути по часам. Отдельно от пути с явной меткой — они не влияют друг на друга. */
const clockState: CounterState = { ms: -1, counter: 0 };
/** Состояние пути с явной меткой времени (фикстуры, тесты, детерминированный засев). */
const explicitState: CounterState = { ms: -1, counter: 0 };

function seedCounter(buf: Uint8Array): number {
  return (((buf[6] as number) << 8) | (buf[7] as number)) & COUNTER_SEED_MASK;
}

/**
 * Возвращает метку времени и счётчик, сохраняя два инварианта:
 *   1. метка никогда не идёт назад (переведённые назад часы не ломают порядок);
 *   2. при переполнении счётчика в пределах миллисекунды заимствуется миллисекунда вперёд
 *      (RFC 9562 §6.2, метод «borrow from the clock») — вместо случайного пересева,
 *      который и ломал монотонность.
 */
function advance(state: CounterState, ts: number, buf: Uint8Array, allowCarry: boolean): number {
  if (ts > state.ms) {
    state.ms = ts;
    state.counter = seedCounter(buf);
    return state.ms;
  }
  // ts <= state.ms: часы стоят, отстали или метка задана явно и повторяется.
  state.counter += 1;
  if (state.counter > MAX_COUNTER) {
    if (allowCarry) {
      state.ms += 1;
      state.counter = seedCounter(buf);
    } else {
      // Явная метка времени обязана вернуться дословно, занимать миллисекунду нельзя.
      // Уникальность держат 62 бита случайного хвоста; монотонность за 4096 в одной
      // фиксированной миллисекунде недостижима по построению — 12 бит счётчика.
      state.counter = seedCounter(buf);
    }
  }
  return state.ms;
}

export function uuidv7(nowMs?: number): string {
  // Часы берутся из time.ts: правило R11 запрещает Date.now() вне единственного дома времени.
  const requested = nowMs ?? clockNowMs();
  if (!Number.isSafeInteger(requested) || requested < 0) {
    throw new TypeError(`uuidv7: timestamp must be a non-negative safe integer, got ${requested}`);
  }

  const buf = new Uint8Array(16);
  globalThis.crypto.getRandomValues(buf.subarray(6, 16));

  const explicit = nowMs !== undefined;
  const state = explicit ? explicitState : clockState;
  if (explicit && requested !== state.ms) {
    // Явная метка сбрасывает состояние: вызывающий волен идти по времени назад.
    state.ms = requested - 1;
  }
  const ts = advance(state, requested, buf, !explicit);
  const counter = state.counter;

  // 48 бит времени, big-endian.
  buf[0] = (ts / 2 ** 40) & 0xff;
  buf[1] = (ts / 2 ** 32) & 0xff;
  buf[2] = (ts / 2 ** 24) & 0xff;
  buf[3] = (ts / 2 ** 16) & 0xff;
  buf[4] = (ts / 2 ** 8) & 0xff;
  buf[5] = ts & 0xff;

  // rand_a = версия (4 бита) + счётчик (12 бит).
  buf[6] = 0x70 | ((counter >>> 8) & 0x0f);
  buf[7] = counter & 0xff;

  // Вариант RFC 4122: два старших бита = 10.
  buf[8] = ((buf[8] as number) & 0x3f) | 0x80;

  const h = HEX;
  return (
    (h[buf[0] as number] as string) +
    (h[buf[1] as number] as string) +
    (h[buf[2] as number] as string) +
    (h[buf[3] as number] as string) +
    '-' +
    (h[buf[4] as number] as string) +
    (h[buf[5] as number] as string) +
    '-' +
    (h[buf[6] as number] as string) +
    (h[buf[7] as number] as string) +
    '-' +
    (h[buf[8] as number] as string) +
    (h[buf[9] as number] as string) +
    '-' +
    (h[buf[10] as number] as string) +
    (h[buf[11] as number] as string) +
    (h[buf[12] as number] as string) +
    (h[buf[13] as number] as string) +
    (h[buf[14] as number] as string) +
    (h[buf[15] as number] as string)
  );
}

export function isUuidV7(s: string): boolean {
  return UUID_V7_RE.test(s);
}

/** Метка времени из первых 48 бит. Бросает на строке, не прошедшей UUID_V7_RE. */
export function uuidv7TimeMs(id: string): number {
  if (!UUID_V7_RE.test(id)) {
    throw new TypeError(`uuidv7TimeMs: not a canonical UUIDv7: ${JSON.stringify(id)}`);
  }
  const hex = id.slice(0, 8) + id.slice(9, 13);
  return Number.parseInt(hex, 16);
}
